import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, Mail, Lock, ArrowRight, Loader2, ShieldCheck,
  Eye, EyeOff, ChevronLeft, User, Phone, Globe, CheckCircle2
} from 'lucide-react';
import './CustomerAuth.css';

const CustomerAuth = () => {
  const [view, setView] = useState('login');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const navigate = useNavigate();

  // Login
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPass, setShowLoginPass] = useState(false);

  // Register
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regPassword, setRegPassword] = useState('');
  const [showRegPass, setShowRegPass] = useState(false);

  // Forgot
  const [forgotEmail, setForgotEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showForgotPass, setShowForgotPass] = useState(false);

  // Live clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Mouse parallax (subtle)
  useEffect(() => {
    const onMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth - 0.5) * 2,
        y: (e.clientY / window.innerHeight - 0.5) * 2,
      });
    };
    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/customer/login`, {
        email: loginEmail,
        password: loginPassword
      });
      const { customerId, customerName } = response.data;
      localStorage.setItem('customerId', customerId);
      localStorage.setItem('customerName', customerName);
      setTimeout(() => {
        setIsLoading(false);
        navigate('/customer-dashboard');
      }, 900);
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Login failed. Please check credentials.');
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/customer/register`, {
        fullName: regName,
        email: regEmail,
        phone: regPhone,
        password: regPassword
      });
      setIsLoading(false);
      alert(response.data.message || 'Registration successful! Please wait for Admin approval.');
      setView('login');
      setRegName(''); setRegEmail(''); setRegPhone(''); setRegPassword('');
    } catch (error) {
      setIsLoading(false);
      alert(error.response?.data?.message || 'Registration failed. Please try again.');
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      alert("Passwords do not match!");
      return;
    }
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      alert('Password reset successfully! Please login.');
      setView('login');
      setForgotEmail(''); setNewPassword(''); setConfirmPassword('');
    }, 1200);
  };

  const formatTime = (date) =>
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

  const formatDate = (date) =>
    date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  const getMeta = () => {
    if (view === 'login') return { title: 'Employer Portal', sub: 'Secure access for hospitality partners' };
    if (view === 'register') return { title: 'Create Account', sub: 'Apply for employer access' };
    return { title: 'Reset Password', sub: 'Create new credentials for your account' };
  };
  const meta = getMeta();

  return (
    <div className="emp-auth">
      {/* Background layers */}
      <div
        className="emp-bg-image"
        style={{ transform: `translate3d(${mousePos.x * -10}px, ${mousePos.y * -6}px, 0) scale(1.05)` }}
      />
      <div className="emp-bg-overlay" />
      <div className="emp-bg-vignette" />
      <div
        className="emp-bg-mesh emp-mesh-1"
        style={{ transform: `translate3d(${mousePos.x * 20}px, ${mousePos.y * 20}px, 0)` }}
      />
      <div
        className="emp-bg-mesh emp-mesh-2"
        style={{ transform: `translate3d(${mousePos.x * -18}px, ${mousePos.y * -18}px, 0)` }}
      />

      {/* Top bar */}
      <div className="emp-top-bar">
        <div className="emp-status">
          <span className="emp-pulse-dot" />
          <span>SECURE SESSION</span>
        </div>
        <div className="emp-top-meta">
          <span className="emp-time">{formatTime(currentTime)}</span>
          <span className="emp-dot">·</span>
          <span>{formatDate(currentTime)}</span>
          <span className="emp-dot">·</span>
          <span className="emp-region"><Globe size={11} /> DXB</span>
        </div>
      </div>

      {/* Content */}
      <div className="emp-content">

        {/* Brand column */}
        <div className="emp-brand-col">
          <div className="emp-brand-mark">
            <div className="emp-brand-icon">
              <Building2 size={20} strokeWidth={2} />
            </div>
            <div className="emp-brand-text">
              <span className="emp-brand-name">HOTELPLACEMENT</span>
              <span className="emp-brand-sub">Employer Network</span>
            </div>
          </div>

          <h1 className="emp-headline">
            The Standard of <br />
            <span className="emp-gold-text">Modern Hospitality</span>
          </h1>

          <p className="emp-subtext">
            Connect with a curated network of hospitality professionals.
            Manage your hiring with efficiency, precision, and discretion.
          </p>

          <div className="emp-foot-meta">
            <div className="emp-foot-item">
              <CheckCircle2 size={13} />
              <span>Verified employer network</span>
            </div>
            <div className="emp-foot-item">
              <Lock size={13} />
              <span>End-to-end encryption</span>
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="emp-form-col">
          <div className="emp-panel">
            <div className="emp-panel-accent" />
            <div className="emp-panel-corner emp-c-tl" />
            <div className="emp-panel-corner emp-c-tr" />
            <div className="emp-panel-corner emp-c-bl" />
            <div className="emp-panel-corner emp-c-br" />

            <div className="emp-security-badge">
              <Lock size={10} />
              <span>TLS 1.3 SECURE</span>
            </div>

            <div className="emp-panel-header">
              <h2>{meta.title}</h2>
              <p>{meta.sub}</p>
            </div>

            {/* LOGIN */}
            {view === 'login' && (
              <form onSubmit={handleLogin} className="emp-form">
                <div className="emp-field">
                  <label>Corporate Email</label>
                  <div className="emp-input-wrap">
                    <Mail className="emp-input-icon" size={16} />
                    <input type="email" placeholder="you@company.com"
                      value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="emp-field">
                  <label>Password</label>
                  <div className="emp-input-wrap">
                    <Lock className="emp-input-icon" size={16} />
                    <input type={showLoginPass ? "text" : "password"} placeholder="••••••••••••"
                      value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
                    <button type="button" className="emp-eye-btn"
                      onClick={() => setShowLoginPass(!showLoginPass)} tabIndex={-1}>
                      {showLoginPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="emp-form-extras">
                  <button type="button" className="emp-forgot-link"
                    onClick={() => setView('forgot')}>
                    Forgot Password?
                  </button>
                </div>

                <button type="submit" className="emp-submit" disabled={isLoading}>
                  <span className="emp-submit-bg" />
                  <span className="emp-submit-content">
                    {isLoading ? (
                      <><Loader2 className="emp-spin" size={17} /><span>Authenticating...</span></>
                    ) : (
                      <><span>Secure Login</span><ArrowRight size={15} className="emp-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <div className="emp-toggle-row">
                  <span>Don't have an account?</span>
                  <button type="button" className="emp-toggle-link" onClick={() => setView('register')}>
                    Register here
                  </button>
                </div>
              </form>
            )}

            {/* REGISTER */}
            {view === 'register' && (
              <form onSubmit={handleRegister} className="emp-form emp-form-compact">
                <div className="emp-field emp-slide">
                  <label>Company / Full Name</label>
                  <div className="emp-input-wrap">
                    <User className="emp-input-icon" size={16} />
                    <input type="text" placeholder="e.g. Marriott International"
                      value={regName} onChange={(e) => setRegName(e.target.value)} required />
                  </div>
                </div>

                <div className="emp-field emp-slide" style={{ animationDelay: '60ms' }}>
                  <label>Corporate Email</label>
                  <div className="emp-input-wrap">
                    <Mail className="emp-input-icon" size={16} />
                    <input type="email" placeholder="hr@company.com"
                      value={regEmail} onChange={(e) => setRegEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="emp-field emp-slide" style={{ animationDelay: '120ms' }}>
                  <label>Phone Number</label>
                  <div className="emp-input-wrap">
                    <Phone className="emp-input-icon" size={16} />
                    <input type="tel" placeholder="+971 50 123 4567"
                      value={regPhone} onChange={(e) => setRegPhone(e.target.value)} required />
                  </div>
                </div>

                <div className="emp-field emp-slide" style={{ animationDelay: '180ms' }}>
                  <label>Password</label>
                  <div className="emp-input-wrap">
                    <Lock className="emp-input-icon" size={16} />
                    <input type={showRegPass ? "text" : "password"} placeholder="Create a secure password"
                      value={regPassword} onChange={(e) => setRegPassword(e.target.value)} required />
                    <button type="button" className="emp-eye-btn"
                      onClick={() => setShowRegPass(!showRegPass)} tabIndex={-1}>
                      {showRegPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="emp-admin-note">
                  <ShieldCheck size={13} />
                  <span>Account requires admin approval before activation</span>
                </div>

                <button type="submit" className="emp-submit" disabled={isLoading}>
                  <span className="emp-submit-bg" />
                  <span className="emp-submit-content">
                    {isLoading ? (
                      <><Loader2 className="emp-spin" size={17} /><span>Submitting...</span></>
                    ) : (
                      <><span>Submit Registration</span><ArrowRight size={15} className="emp-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="emp-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              </form>
            )}

            {/* FORGOT */}
            {view === 'forgot' && (
              <form onSubmit={handleForgotPassword} className="emp-form">
                <div className="emp-field emp-slide">
                  <label>Registered Email</label>
                  <div className="emp-input-wrap">
                    <Mail className="emp-input-icon" size={16} />
                    <input type="email" placeholder="Enter your email"
                      value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required />
                  </div>
                </div>

                <div className="emp-field emp-slide" style={{ animationDelay: '60ms' }}>
                  <label>New Password</label>
                  <div className="emp-input-wrap">
                    <Lock className="emp-input-icon" size={16} />
                    <input type={showForgotPass ? "text" : "password"} placeholder="New secure password"
                      value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required />
                    <button type="button" className="emp-eye-btn"
                      onClick={() => setShowForgotPass(!showForgotPass)} tabIndex={-1}>
                      {showForgotPass ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>

                <div className="emp-field emp-slide" style={{ animationDelay: '120ms' }}>
                  <label>Confirm Password</label>
                  <div className="emp-input-wrap">
                    <Lock className="emp-input-icon" size={16} />
                    <input type={showForgotPass ? "text" : "password"} placeholder="Confirm new password"
                      value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required />
                  </div>
                </div>

                <button type="submit" className="emp-submit" disabled={isLoading}>
                  <span className="emp-submit-bg" />
                  <span className="emp-submit-content">
                    {isLoading ? (
                      <><Loader2 className="emp-spin" size={17} /><span>Updating...</span></>
                    ) : (
                      <><span>Update Password</span><ArrowRight size={15} className="emp-submit-arrow" /></>
                    )}
                  </span>
                </button>

                <button type="button" className="emp-back-link" onClick={() => setView('login')}>
                  <ChevronLeft size={14} /> <span>Back to <strong>Login</strong></span>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerAuth;